import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { Controller } from 'react-hook-form'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { selectChallengeTypes } from 'store/challengeTypes/slice'
import { getChallengeTypes } from 'store/challengeTypes/thunks'
import { getSponsoredChallengeTypeName } from 'utils/getChallengeTypeName'
import { KeyboardArrowDown } from '@mui/icons-material'
import type { ChallengeTypeInfo } from 'api/types'
interface Props {
  name: string
  control: any
  value?: string
  disabled?: boolean
}

export default function ChallengeTypeSelect({ name, control, value, disabled }: Props): JSX.Element {
  const [selected, setSelected] = useState<string | null>(value || null)
  const dispatch = useDispatch()
  const intl = useIntl()
  const { error, status, types } = useSelector(selectChallengeTypes)
  const challengeTypeItems = Array.isArray(types) ? types : []

  useEffect(() => {
    if (status === 'idle') {
      dispatch(getChallengeTypes())
    }
    if (value && value !== selected) {
      setSelected(value)
    }
  }, [status, dispatch, value, selected])

  const getSortedTypes = () => {
    const filtered = challengeTypeItems.filter((c: ChallengeTypeInfo) => c.challengeType !== null)
    return filtered.sort((a, b) => getChallengeTypeLabel(a).localeCompare(getChallengeTypeLabel(b)))
  }

  const getChallengeTypeLabel = (challengeType: ChallengeTypeInfo) => {
    if (!challengeType || !challengeType.challengeType) {
      return ''
    }
    return getSponsoredChallengeTypeName(challengeType.challengeType, intl)
  }

  const getLabelWithValue = (value: any) => {
    const type = challengeTypeItems.find((challengeType: ChallengeTypeInfo) => value === challengeType.challengeType)
    return type ? getLabel(type) : ''
  }

  const getLabel = (challengeType: ChallengeTypeInfo) => {
    return getChallengeTypeLabel(challengeType)
  }

  const getValue = () => {
    return selected ? selected : ''
  }

  return (
    <>
      <InputContainer margin="normal" fullWidth required>
        <FieldLabel>
          <FormattedMessage id={'challenges.type'} />
        </FieldLabel>
        <Controller
          control={control}
          name={name}
          defaultValue={selected || ''}
          render={({ field: { onChange, value: fieldValue } }) => (
            <Select
              onChange={(e) => {
                const newValue = e.target.value as string
                setSelected(newValue)
                onChange(newValue)
              }}
              error={error !== null}
              required
              variant="outlined"
              displayEmpty
              value={fieldValue !== undefined ? fieldValue : getValue()}
              renderValue={(value: any) =>
                value ? (
                  getLabelWithValue(value)
                ) : (
                  <Placeholder>{intl.formatMessage({ id: 'strings.select' })}</Placeholder>
                )
              }
              disabled={disabled}
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',

                '& fieldset': {
                  borderColor: '#e4e4e7',
                },

                '&:hover fieldset': {
                  borderColor: '#e4e4e7',
                },

                '&.Mui-focused fieldset': {
                  borderColor: '#e4e4e7',
                },

                '& .MuiOutlinedInput-input': {
                  borderRadius: '8px',
                  padding: '10px 12px',
                },
                '& .MuiSelect-icon': {
                  color: '#8A8A8E',
                  fontSize: 20,
                },
              }}
              IconComponent={KeyboardArrowDown}
            >
              {status === 'loading' && (
                <MenuItem value="">
                  <FormattedMessage id="strings.loading" />
                  ...
                </MenuItem>
              )}
              {getSortedTypes().map((challengeType: ChallengeTypeInfo) => (
                <MenuItem key={challengeType.challengeType} value={challengeType.challengeType}>
                  {getChallengeTypeLabel(challengeType)}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </InputContainer>
    </>
  )
}

const InputContainer = styled(FormControl)`
  margin: 20px 0 0;
`

const FieldLabel = styled(InputLabel)`
  margin-top: -20px;
  transition: none;
  transform: none;
`

const Placeholder = styled.span`
  color: ${(props) => props.theme.customPalette.mediumGray};
`
